import React from "react";
import RequestEngine from "../../core/RequestEngine";
import strings from "../../core/translate";
import { apiErrorMessage } from "../../shared/api-error";
import {
  FormSection,
  FormGrid,
  TextField,
  SelectField,
  SwitchField,
  FormAlert,
  FormActions,
} from "../../components/Form/FormKit";

// The currency this garage prices in, VAT, the delivery-date traffic-light
// thresholds, and which payment methods it accepts — one record per branch.
export default class GarageSetupForm extends React.Component {
  constructor(props) {
    super(props);
    const item = props.item || {};
    this.state = {
      id: item.id || "",
      version: item.version,
      currencyId: (item.currency && item.currency.id) || "",
      hasVat: !!item.hasVat,
      // the Owner's say over whether Managers may price work orders
      managersCanPrice: item.managersCanPrice ?? true,
      vatPercentage:
        item.vatPercentage === null || item.vatPercentage === undefined
          ? ""
          : item.vatPercentage,
      greenThresholdHours: item.greenThresholdHours ?? 24,
      yellowThresholdHours: item.yellowThresholdHours ?? 8,
      redThresholdHours: item.redThresholdHours ?? 2,
      paymentMethodIds: (item.paymentMethods || []).map((it) => it.id),
      paymentMethodOptions: [],
      currencyOptions: [],
      loading: false,
      error: "",
      errors: {},
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    this.fetchPaymentMethods();
    this.fetchCurrencies();
  }

  fetchCurrencies = async () => {
    const response = await this.engine.getItem("currencies");
    if (response && response.status === 200) {
      // a currency the admin turned off stays selectable when this garage is
      // already on it, so editing the setup cannot silently drop it
      const items = (response.data.items || []).filter(
        (it) => it.isActive || it.id === this.state.currencyId
      );
      this.setState({
        currencyOptions: items.map((it) => ({
          value: it.id,
          label: `${it.name} (${it.symbol})`,
        })),
      });
    }
  };

  fetchPaymentMethods = async () => {
    const response = await this.engine.getItem("payment-methods");
    if (response && response.status === 200) {
      this.setState({
        paymentMethodOptions: response.data.items
          .filter((it) => it.isActive)
          .map((it) => ({
            value: it.id,
            label: it.name,
          })),
      });
    }
  };

  validate() {
    const {
      hasVat,
      vatPercentage,
      greenThresholdHours,
      yellowThresholdHours,
      redThresholdHours,
    } = this.state;
    const errors = {};

    if (hasVat && `${vatPercentage}`.trim() === "") {
      errors.vatPercentage = strings.vat_percentage_hint;
    }
    if (hasVat && (Number(vatPercentage) < 0 || Number(vatPercentage) > 100)) {
      errors.vatPercentage = strings.vat_percentage_hint;
    }

    [
      ["greenThresholdHours", greenThresholdHours],
      ["yellowThresholdHours", yellowThresholdHours],
      ["redThresholdHours", redThresholdHours],
    ].forEach(([field, value]) => {
      if (value === "" || value === null || Number(value) < 0) {
        errors[field] = strings.select;
      }
    });

    if (
      !errors.greenThresholdHours &&
      !errors.yellowThresholdHours &&
      !errors.redThresholdHours &&
      !(
        Number(redThresholdHours) <= Number(yellowThresholdHours) &&
        Number(yellowThresholdHours) <= Number(greenThresholdHours)
      )
    ) {
      errors.yellowThresholdHours = strings.delivery_colors_hint;
    }

    return errors;
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    const {
      id,
      version,
      currencyId,
      hasVat,
      managersCanPrice,
      vatPercentage,
      greenThresholdHours,
      yellowThresholdHours,
      redThresholdHours,
      paymentMethodIds,
    } = this.state;
    this.setState({ loading: true, error: "", errors: {} });

    const payload = {
      // left empty on a new setup, the backend puts the garage on the platform
      // default currency
      ...(currencyId && { currencyId }),
      hasVat,
      managersCanPrice,
      ...(hasVat && { vatPercentage: Number(vatPercentage) }),
      greenThresholdHours: Number(greenThresholdHours),
      yellowThresholdHours: Number(yellowThresholdHours),
      redThresholdHours: Number(redThresholdHours),
      paymentMethodIds,
    };

    try {
      const response = id
        ? await this.engine.updateItem(this.props.pathname, id, {
            ...payload,
            version,
          })
        : await this.engine.saveItem(this.props.pathname, payload);

      if (response && (response.status === 200 || response.status === 201)) {
        this.setState({ loading: false });
        this.props.onSaved(response.data);
        return;
      }
    } catch (err) {
      this.setState({ error: apiErrorMessage(err) });
    }
    this.setState({ loading: false });
  };

  render() {
    const {
      currencyId,
      currencyOptions,
      hasVat,
      managersCanPrice,
      vatPercentage,
      greenThresholdHours,
      yellowThresholdHours,
      redThresholdHours,
      paymentMethodIds,
      paymentMethodOptions,
      loading,
      error,
      errors,
    } = this.state;

    return (
      <form className="mform" onSubmit={this.handleSubmit}>
        <FormSection title={strings.currency} icon="nc-icon nc-money-coins">
          <FormGrid full>
            <SelectField
              help={strings.garage_currency_hint}
              value={currencyId}
              options={currencyOptions}
              onChange={(value) => this.setState({ currencyId: value })}
            />
          </FormGrid>
        </FormSection>

        <FormSection title={strings.work_orders} icon="nc-icon nc-settings-tool-66">
          <SwitchField
            label={strings.managers_can_price}
            help={strings.managers_can_price_hint}
            checked={managersCanPrice}
            onChange={(value) => this.setState({ managersCanPrice: value })}
          />
        </FormSection>

        <FormSection title={strings.vat} icon="nc-icon nc-money-coins">
          <SwitchField
            label={strings.has_vat}
            checked={hasVat}
            onChange={(value) => this.setState({ hasVat: value })}
          />
          {hasVat && (
            <FormGrid full>
              <TextField
                label={strings.vat_percentage}
                type="number"
                step="0.01"
                min="0"
                required
                value={vatPercentage}
                onChange={(value) => this.setState({ vatPercentage: value })}
                error={errors.vatPercentage}
              />
            </FormGrid>
          )}
        </FormSection>

        <FormSection title={strings.delivery_colors} icon="nc-icon nc-time-alarm">
          <p className="mfield__help">{strings.delivery_colors_hint}</p>
          <FormGrid>
            <TextField
              label={strings.green_threshold_hours}
              type="number"
              min="0"
              required
              value={greenThresholdHours}
              onChange={(value) =>
                this.setState({ greenThresholdHours: value })
              }
              error={errors.greenThresholdHours}
            />
            <TextField
              label={strings.yellow_threshold_hours}
              type="number"
              min="0"
              required
              value={yellowThresholdHours}
              onChange={(value) =>
                this.setState({ yellowThresholdHours: value })
              }
              error={errors.yellowThresholdHours}
            />
          </FormGrid>
          <FormGrid full>
            <TextField
              label={strings.red_threshold_hours}
              type="number"
              min="0"
              required
              value={redThresholdHours}
              onChange={(value) => this.setState({ redThresholdHours: value })}
              error={errors.redThresholdHours}
            />
          </FormGrid>
        </FormSection>

        <FormSection
          title={strings.accepted_payment_methods}
          icon="nc-icon nc-credit-card"
        >
          <FormGrid full>
            <SelectField
              isMulti
              value={paymentMethodIds}
              options={paymentMethodOptions}
              onChange={(value) => this.setState({ paymentMethodIds: value })}
            />
          </FormGrid>
        </FormSection>

        <FormAlert>{error}</FormAlert>
        <FormActions onCancel={this.props.onCancel} loading={loading} />
      </form>
    );
  }
}
